import Card from "./Card";
import Button from "Components/Common/Button/Button";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import Table, { Column } from "Components/Table/Table";
import {
  DrawDetail,
  DrawG703Row,
  MoneyField,
  fmtMoney,
} from "../DrawHistory.types";
import EmptyState from "Components/Common/EmptyState";
import EmptyLienWaiverIcon from "Assets/Img/EmptyLienWaiverIcon";

interface G703TabProps {
  drawDetail: DrawDetail | null;
  loading: boolean;
  onExportExcel: () => void;
  exportingExcel?: boolean;
}

const G703Tab = ({
  drawDetail,
  loading,
  onExportExcel,
  exportingExcel = false,
}: G703TabProps) => {
  const rows = drawDetail?.lineItems?.rows ?? [];
  const currencySymbol =
    rows[0]?.totalCompletedAndStored?.currency?.symbol ?? "$";
  const drawNumberLabel = drawDetail
    ? String(drawDetail.draw.drawNumber).padStart(2, "0")
    : "—";

  const sumColumn = (key: keyof DrawG703Row): MoneyField => ({
    amount: rows.reduce(
      (sum, row) => sum + ((row[key] as MoneyField | undefined)?.amount ?? 0),
      0,
    ),
    currency: { symbol: currencySymbol },
  });

  const columns: Column<DrawG703Row>[] = [
    {
      header: "#",
      render: (row) => row.aiaItemCode ?? row.aiaItem?.itemCode ?? "—",
    },
    {
      header: "Description",
      className: "left",
      render: (row) => row.description ?? row.aiaItem?.itemName ?? "—",
    },
    { header: "Scheduled Value", render: (row) => fmtMoney(row.sovValue) },
    {
      header: "Previous Application",
      render: (row) => fmtMoney(row.previousBilled),
    },
    {
      header: "This Period",
      render: (row) => fmtMoney(row.currentApplication),
    },
    {
      header: "Materials Stored",
      render: (row) => fmtMoney(row.currentStoredMaterial),
    },
    {
      header: "Total Completed & Stored",
      render: (row) => fmtMoney(row.totalCompletedAndStored),
    },
    { header: "%", render: (row) => `${row.percentageComplete ?? 0}%` },
    {
      header: "Balance to Finish",
      render: (row) => fmtMoney(row.balanceToFinish),
    },
  ];

  const footerRow = [
    "",
    "Grand Total",
    fmtMoney(sumColumn("sovValue")),
    fmtMoney(sumColumn("previousBilled")),
    fmtMoney(sumColumn("currentApplication")),
    fmtMoney(sumColumn("currentStoredMaterial")),
    fmtMoney(sumColumn("totalCompletedAndStored")),
    "",
    fmtMoney(sumColumn("balanceToFinish")),
  ];

  return (
    <>
      <div className="sectionTitle">AIA G703 — Continuation Sheet</div>
      <div className="sectionSub">
        Line items billed in Draw #{drawNumberLabel}.
      </div>

      <Card
        title={`Line Items — Draw #${drawNumberLabel}`}
        action={
          <Button
            type="button"
            className="btn secondary sm"
            onClick={onExportExcel}
            disabled={exportingExcel}
          >
            {exportingExcel ? "Generating…" : "📊 Export .xlsx"}
          </Button>
        }
      >
        {loading ? (
          <TableSkeleton cols={9} rows={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            noBorder
            icon={<EmptyLienWaiverIcon />}
            title="No line items billed on this draw."
          />
        ) : (
          <div className="aiaTableWrap">
            <Table
              tableClassName="aiaTable"
              data={rows}
              columns={columns}
              footerRow={footerRow}
            />
          </div>
        )}
      </Card>
    </>
  );
};

export default G703Tab;
